import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";

const normalize = (v) =>
  v === null || v === undefined ? "" : String(v);

const Switch = ({
  values = "",
  handleChange,
  field,
  ValueOne,
  ValueTwo,
  LabelOne,
  LabelTwo,
  error = "",
  width = null,
}) => {
  const valueOneStr = normalize(ValueOne);
  const valueTwoStr = normalize(ValueTwo);
  const valuesStr = normalize(values);

  const isValidValue =
    valuesStr === valueOneStr || valuesStr === valueTwoStr;

  const [checked, setChecked] = useState(
    isValidValue ? valuesStr : valueOneStr
  );

  /* ----------------------------------------------------
     Sync external value → internal state
  ---------------------------------------------------- */
  useEffect(() => {
    if (isValidValue) {
      setChecked(valuesStr);
    } else {
      setChecked(valueOneStr);

      // Force default value to parent form
      handleChange({
        target: {
          name: field.name,
          value: ValueOne,
        },
      });
    }
  }, [
    valuesStr,
    isValidValue,
    valueOneStr,
    ValueOne,
    handleChange,
    field.name,
  ]);

  /* ----------------------------------------------------
     Radio change handler
  ---------------------------------------------------- */
  const onRadioChange = useCallback(
    (e) => {
      setChecked(e.target.value);
      handleChange(e);
    },
    [handleChange]
  );

  /* ----------------------------------------------------
     Width handling
  ---------------------------------------------------- */
  const containerClass =
    typeof width === "string" && width.startsWith("col-")
      ? width
      : field.fieldSize || "col-12";

  const inlineStyle =
    typeof width === "number"
      ? { width: `${width}px` }
      : typeof width === "string" && !width.startsWith("col-")
        ? { width }
        : undefined;

  return (
    <div className={containerClass} style={inlineStyle} >
      <fieldset className="border-0 p-0 m-0">
        {field.label && (
          <legend className="form-label text-muted fs-6">
            {field.label}
            {field.required && (
              <span className="text-danger ms-1">*</span>
            )}
          </legend>
        )}

        <div className="btn-group" role="group">
          <input
            type="radio"
            className="btn-check"
            name={field.name}
            id={`${field.name}-${valueOneStr}`}
            value={valueOneStr}
            checked={checked === valueOneStr}
            onChange={onRadioChange}
            disabled={field.disabled}
          />
          <label
            className="btn btn-outline-primary"
            htmlFor={`${field.name}-${valueOneStr}`}
          >
            {LabelOne}
          </label>

          <input
            type="radio"
            className="btn-check"
            name={field.name}
            id={`${field.name}-${valueTwoStr}`}
            value={valueTwoStr}
            checked={checked === valueTwoStr}
            onChange={onRadioChange}
            disabled={field.disabled}
          />
          <label
            className="btn btn-outline-primary"
            htmlFor={`${field.name}-${valueTwoStr}`}
          >
            {LabelTwo}
          </label>
        </div>
      </fieldset>

      {error && <div className="text-danger mt-1">{error}</div>}
    </div>
  );
};

/* ----------------------------------------------------
   PropTypes
---------------------------------------------------- */
Switch.propTypes = {
  values: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),

  handleChange: PropTypes.func.isRequired,

  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    fieldSize: PropTypes.string,
    required: PropTypes.bool,
  }).isRequired,

  ValueOne: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,

  ValueTwo: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,

  LabelOne: PropTypes.string.isRequired,
  LabelTwo: PropTypes.string.isRequired,

  error: PropTypes.string,

  width: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
};

export default Switch;
